/**
 * The labels of record of the page "Learn from sales" (`/learn-from-sales`, 20 Oct 2026):
 * every text the page prints. The page shows which inquiry I2Q linked to which F&O
 * quotation and sales order, made by which sales person, how the I2Q draft differs line by
 * line from what sales quoted, and what I2Q learns from it.
 *
 * Help, the documentation and the case page quote these texts verbatim, and
 * `tests/ste.test.mjs` lints this file with the house rules: short sentences, no long dash,
 * one word for one thing. Words of record (lead decisions S3 and S7): "I2Q matched your
 * quotation", never a score or an accuracy of a person; "Not shown" for the sales person of
 * Germany and France; the link certainty words "Exact", "Strong" and "Inferred" of the
 * glossary.
 */

export const LFS_TITLE = "Learn from sales";
/** The header subtitle. */
export const LFS_SUBTITLE =
  "Which inquiries sales quoted in F&O, how the I2Q draft differs, and what I2Q learns from it.";

/** The states of the page that are not data. */
export const LFS_NOTICE_LABELS = {
  noRight: "This page is for managers and admins.",
  emptyWindow: "No inquiry in this period. Pick a longer period or another area.",
  noLinks: "No inquiry of this period has a link to a quotation in F&O yet.",
  emptyFiltered: "No inquiry fits these filters.",
  noPeople: "No sales person to show for this period.",
  noLearnings: "I2Q has no learnings for these filters yet.",
  noDiff: "No line to compare.",
  notFound: "This inquiry has no link to a quotation in F&O, or it is not in your areas.",
  sampleData: "Sample figures of the local development server.",
} as const;

/** The five tabs, in their order. */
export const LFS_TABS = ["overview", "trail", "inquiries", "people", "learnings"] as const;
export type LfsTab = (typeof LFS_TABS)[number];

export const TAB_LABELS: Record<LfsTab, string> = {
  overview: "Overview",
  // ste-allow: product-term (the name of the tab the product owners chose, 4 Nov 2026)
  trail: "Email to order",
  inquiries: "Inquiries",
  people: "Sales people",
  learnings: "Learnings",
};
export const TABS_LABEL = "Views of the page";

/** The filter bar. */
export const FILTER_LABELS = {
  area: "Area",
  allAreas: "All areas",
  window: "Received in",
  source: "How linked",
  allSources: "All links",
  order: "Sales order",
  anyOrder: "With or without",
  withOrder: "With a sales order",
  withoutOrder: "No sales order yet",
  agreement: "Lines matched",
  anyAgreement: "All",
  learn: "Learnings",
  anyLearn: "All",
  openInI2q: "Still open in I2Q",
  sort: "Order of the list",
  takerChip: "Sales person",
  clearTaker: "Show all sales people",
  status: "Status",
  allStatuses: "Learnings and conflicts",
  kind: "Kind",
  allKinds: "Items and customers",
  moreFilters: "More filters",
} as const;

/** The windows of the filter "Received in", in days. */
export const LFS_WINDOWS = [7, 30, 90, 365] as const;
export type LfsWindow = (typeof LFS_WINDOWS)[number];

export const WINDOW_LABELS: Record<LfsWindow, string> = {
  7: "Last 7 days",
  30: "Last 30 days",
  90: "Last 90 days",
  365: "Last 365 days",
};

/** The order of the list of inquiries. */
export const SORT_LABELS = {
  received_desc: "Newest first",
  agreement_asc: "Fewest lines matched first",
  agreement_desc: "Most lines matched first",
} as const;

/** How I2Q linked the inquiry to what sales quoted (`rfq_case_ground_truth.source`). */
export const SOURCE_LABELS = {
  fo_quotation: "Quotation number on the reply PDF",
  fo_sales_order: "Sales order on the reply PDF",
  fo_reply_ref: "Quotation number in the reply text",
  fo_thread: "Quotation number in the mail thread",
  fo_contact: "Sender is the contact of the quotation",
  mail_offer: "Offer table in the mail thread",
} as const;

/** How sure the link is: the words of the glossary. */
export const CERTAINTY_LABELS = {
  exact: "Exact",
  strong: "Strong",
  inferred: "Inferred",
} as const;

/** What F&O says about the quotation. */
export const QUOTE_STATUS_LABELS = {
  open: "Open",
  won: "Won",
  lost: "Lost",
} as const;

/** The bands of the filter "Lines matched". */
export const AGREEMENT_LABELS = {
  none: "No line matched",
  partial: "Some lines matched",
  full: "All lines matched",
} as const;

/** How the I2Q customer compares with the account of the quotation. */
export const CUSTOMER_MATCH_LABELS = {
  same: "Same customer",
  different: "Other customer",
  i2q_none: "I2Q had no customer",
  no_fo: "No customer in F&O",
} as const;

/** The result of one row of the line difference. */
export const VERDICT_LABELS = {
  same_item: "Same item",
  other_item: "Other item",
  missed: "Missed by I2Q",
  extra: "Extra in I2Q",
  qty_differs: "Quantity differs",
  price_differs: "Price differs",
  not_quoted: "Not quoted",
} as const;

/** What each result means, for the overview and the help. */
export const VERDICT_HINTS = {
  same_item: "I2Q had the item that sales quoted.",
  other_item: "Sales quoted another item for this line.",
  missed: "Sales quoted a line that I2Q did not have.",
  extra: "I2Q had a line that sales did not quote.",
  qty_differs: "Same item, but sales quoted another quantity.",
  price_differs: "Same item, but sales quoted another price.",
  not_quoted: "I2Q had no item for this line, and sales did not quote it.",
} as const;

/** The status of a learning (`rfq_truth_learning.status`). */
export const LEARNING_STATUS_LABELS = {
  observed: "Observed",
  confirmed: "Confirmed",
  proposed: "Proposed",
  active: "Active",
  conflict: "Conflict",
  unaligned: "Not aligned",
  retracted: "Retracted",
} as const;

/** What each status means, for the tab "Learnings" and the help. */
export const LEARNING_STATUS_HINTS = {
  observed: "I2Q would learn this. Nothing is applied yet.",
  confirmed: "I2Q already had what sales quoted.",
  proposed:
    "I2Q wrote it as a proposed alias. A manager approves it, or two quotations and an order make it active.",
  active: "I2Q uses it for new inquiries.",
  conflict:
    "Another quotation gives another value for the same text. I2Q applies only a value that a manager approved.",
  unaligned: "I2Q could not tell which quoted line belongs to this inquiry line.",
  retracted: "A manager took it back.",
} as const;

/**
 * The hint of a proposed CUSTOMER learning: the contact memory has no proposed state, so
 * its pair is in use at once (G4, finding R7 of 20 Oct 2026).
 */
export const PROPOSED_PAIR_HINT = "I2Q uses this pair for new inquiries. A manager can retract it.";

export const LEARNING_KIND_LABELS = {
  item: "Item",
  customer: "Customer",
} as const;

/** Why a learning is observed only. */
export const OBSERVE_REASON_LABELS: Record<string, string> = {
  fo_contact: "Observed only: the link is by the sender contact.",
  modified_after_reply: "Observed only: sales changed the quotation after the reply.",
  not_on_reply_document: "Observed only: the quotation that sales sent does not show this value.",
};

/** The KPI row of the tab "Overview". */
export const KPI_LABELS = {
  inquiries: "Inquiries",
  inquiriesCaption: "Checked against F&O in this period",
  linked: "Linked to an F&O quotation",
  ordered: "Became a sales order",
  matched: "I2Q matched sales on all lines",
  learnings: "Learnings",
  conflicts: "Conflicts",
  conflictsCaption: "Quotations disagree. I2Q applies none of them.",
  openInI2q: "Still open in I2Q, already quoted in F&O",
  showOpen: "Show these inquiries",
} as const;

/** The sections of the tab "Overview". */
export const OVERVIEW_LABELS = {
  funnel: "From inquiry to sales order, per area",
  funnelInquiries: "Inquiries",
  funnelLinked: "Linked to a quotation",
  funnelOrder: "Became a sales order",
  sources: "How I2Q linked the inquiries",
  differences: "Where I2Q differs from sales",
  differencesNote: "Counted over the lines of the linked inquiries of this period.",
  switches: "Learning from sales",
  observe: "Observe",
  apply: "Propose aliases",
  autoActivate: "Activate by itself",
  multiAccount: "Mark several accounts",
  on: "On",
  off: "Off",
} as const;

/** The columns of the tab "Inquiries". */
export const INQUIRY_COLUMN_LABELS = {
  received: "Received",
  area: "Area",
  customer: "Customer: I2Q and F&O",
  quotation: "Quotation",
  order: "Sales order",
  person: "Sales person",
  linked: "How linked",
  agreement: "Lines matched",
  learnings: "Learnings",
  open: "In I2Q",
  rows: "Inquiries linked to what sales quoted",
} as const;

/** The pages of the list. */
export const PAGING_LABELS = {
  inquiries: "Pages of the inquiries",
  learnings: "Pages of the learnings",
} as const;

/** The words in a cell. */
export const CELL_LABELS = {
  notShown: "Not shown",
  notKnown: "Not known",
  none: "None",
  openInI2q: "Still open in I2Q",
  conflict: "conflict",
  conflicts: "conflicts",
  notInInquiry: "Not in the inquiry",
  lines: "lines",
  open: "Open",
  noText: "No text",
  changedAfterReply: "Sales changed the quotation after the reply",
} as const;

/** The drawer of one inquiry. */
export const DRAWER_LABELS = {
  title: "Inquiry and quotation",
  facts: "The link",
  received: "Received",
  area: "Area",
  customer: "Customer",
  quotation: "Quotation",
  quoteMade: "Quotation made",
  order: "Sales order",
  person: "Sales person",
  linked: "How linked",
  agreement: "Lines matched",
  changed: "Changed after the reply",
  yes: "Yes",
  diff: "Line by line",
  learns: "What I2Q learns from this",
  openCase: "Open the case",
  close: "Close",
} as const;

/**
 * Round 3 (20 Oct 2026, goal A): the lines the customer ordered. A quoted line reads
 * "Ordered" or "Not ordered" only when the link has a sales order with its lines read.
 *
 * Round 4 (21 Oct 2026): the order lines are the lines of the SALES ORDER when I2Q has read
 * them (`order_lines_source` "sales_order"). A quoted line then reads "Ordered", "Removed"
 * or "Cancelled", an item with another quantity "Quantity changed", and the lines the
 * quotation did not have are "Added on the sales order". Until I2Q reads them, the order
 * lines are the lines of the quotation at the order (the words of round 3).
 */
export const ORDER_LINE_LABELS = {
  ordered: "Ordered",
  notOrdered: "Not ordered",
  removed: "Removed",
  cancelled: "Cancelled",
  qtyChanged: "Quantity changed",
  orderedHint: "The sales order has this line.",
  removedHint: "The sales order does not have this line. It was removed after the order.",
  cancelledHint: "The line is cancelled on the sales order.",
  notCountedHint:
    "The sales order has this line with quantity 0 or without an order status. It does not count as ordered.",
  quoteOrderedHint: "The quotation had this line when it became a sales order.",
  notOrderedHint: "The quotation did not have this line when it became a sales order.",
  orderedLines: "Ordered lines",
  draftHad: "The draft had the ordered items",
  orderOnly: "Ordered, not on the quotation when I2Q linked it",
  added: "Added on the sales order",
  addedHint: "The quotation did not have this item.",
  credit: "Credit lines on the sales order",
  creditHint: "Credit line on the sales order. It does not count as ordered.",
  fallbackNote: "Lines of the quotation at the order. I2Q has not read the sales order lines yet.",
  changed: "Changed after the order",
  kpiCaption: "Lines of the sales order: open, delivered or invoiced.",
  kpiCaptionQuote: "Lines of the quotation the sales order was made from.",
  perArea: "The draft had the ordered items, per area",
  learningHint: "The sales order has the item that I2Q learned.",
  /** The note the server wrote on a learning that decision D6 made active (round 3). */
  activeNote: "The line was on the quotation when it became a sales order.",
  /** The hint of "Active" for such a learning (decision D6, one quotation). */
  activeHint: "Active: the line was on the quotation when it became a sales order.",
  /** The note of D6 on the lines of the sales order (round 4), and its hint. */
  activeNoteSalesOrder: "The sales order has this line.",
  activeHintSalesOrder: "Active: the sales order has this line.",
  /** The note of the take-back of D6 (round 4): the alias is proposed again. */
  takeBackNote: "The sales order does not have this line. I2Q set this learning back to proposed.",
} as const;

/** "14 of 16 quoted lines ordered" (the drawer and "Quoted in F&O"). */
export function orderedLinesText(ordered: number, quoted: number): string {
  return `${ordered} of ${quoted} quoted lines ordered`;
}

/** "Quoted 5, ordered 3." (the hint of "Quantity changed", quantities formatted). */
export function qtyChangedText(quoted: string, ordered: string): string {
  return `Quoted ${quoted}, ordered ${ordered}.`;
}

/** "120 inquiries use the quotation lines." (the KPI caption while some links fall back). */
export function fallbackLinksText(links: string): string {
  return `${links} inquiries use the quotation lines.`;
}

/**
 * "88 of 703 orders. Removed or cancelled lines 44, quantity changed 36, added lines 22."
 * (the caption of the KPI "Changed after the order", counts formatted).
 */
export function changedOrdersText(
  changed: string,
  orders: string,
  removed: string,
  qty: string,
  added: string,
): string {
  return (
    `${changed} of ${orders} orders. Removed or cancelled lines ${removed}, ` +
    `quantity changed ${qty}, added lines ${added}.`
  );
}

/** "Removed 1, cancelled 1, quantity changed 1, added 2" (the drawer fact, counts formatted). */
export function orderChangesText(
  removed: number,
  cancelled: number,
  qty: number,
  added: number,
): string {
  return `Removed ${removed}, cancelled ${cancelled}, quantity changed ${qty}, added ${added}`;
}

/**
 * Round 3 (20 Oct 2026, goal B): the customer's purchase order number of the sales order
 * ("Customer PO", business data, shown for every area) and the earliest mail of the
 * customer that names it ("In purchase order mail"). The mail is evidence only: it changes
 * no link, no learning and no customer.
 */
export const PO_LABELS = {
  customerPo: "Customer PO",
  poMail: "In purchase order mail",
  poMailHint: "The first order mail of the customer that names this PO, else the first mail.",
  poMailOtherArea: "In a mail of another area",
  kpi: "Orders with a customer PO",
} as const;

/** "Named in a purchase order mail: 447 of 659." (the caption of the KPI, counts formatted). */
export function poMailCaption(inMail: string, withKey: string): string {
  return `Named in a purchase order mail: ${inMail} of ${withKey}.`;
}

/** The block "Quoted in F&O" of the case page. */
export const QUOTED_IN_FO_LABELS = {
  title: "Quoted in F&O",
} as const;

/** The table of the line difference (the drawer and the case page). */
export const DIFF_COLUMN_LABELS = {
  inquiry: "Inquiry text",
  i2q: "I2Q draft",
  sales: "Sales quoted",
  verdict: "Result",
  qty: "qty",
  table: "The I2Q draft and the quotation of sales, line by line",
} as const;

/** The tab "Sales people". */
export const PEOPLE_LABELS = {
  person: "Sales person",
  areas: "Areas",
  linked: "Linked inquiries",
  ordered: "Became a sales order",
  matched: "I2Q matched your quotation",
  learnings: "Learnings from the quotations",
  table: "Sales people and their linked inquiries",
  showInquiries: "Show the inquiries",
  note: "Most linked inquiries first. This is no ranking and no score of a person.",
} as const;

/** The line under the table for the areas whose people are not shown (S3). */
export function hiddenPeopleText(areas: readonly string[], linked: number): string {
  const names = areas.join(", ");
  return `Sales people of ${names} are not shown. Their ${linked} linked inquiries count in the other tabs.`;
}

/** The tab "Learnings" and the drawer. */
export const LEARNING_LABELS = {
  kind: "Kind",
  text: "Inquiry text or sender",
  i2q: "I2Q had",
  sales: "Sales quoted",
  status: "Status",
  evidence: "From",
  hits: "Used",
  table: "What I2Q learns from the quotations of sales",
  approve: "Approve",
  retract: "Retract",
  byManager: "A manager approves.",
  caseLink: "Case",
  hitsAfter: "times since it is active",
  account: "Account",
  applyOff: "I2Q applies learnings only when the switch truth_learn_apply is on.",
} as const;

/**
 * Decision D5 (20 Oct 2026): a sender who buys for more than one customer account carries
 * the mark "Several accounts" in the contact memory. I2Q then does not use the sender alone
 * to find the customer, and writes no contact pair for the sender.
 */
export const MULTI_ACCOUNT_LABELS = {
  badge: "Several accounts",
  hint: "The sender buys for more than one customer account. I2Q does not use the sender alone to find the customer.",
  clear: "Clear the mark",
  mark: "Mark: several accounts",
  off: "I2Q uses the mark only when the switch truth_learn_multi_account is on.",
} as const;

/** "3 senders buy for several accounts" (the caption over the learnings). */
export function multiAccountSendersText(count: number): string {
  return count === 1
    ? "1 sender buys for several accounts."
    : `${count} senders buy for several accounts.`;
}

/** Why a decision on a learning did not save. */
export const LEARNING_FAILURE_LABELS = {
  changed: "This learning changed in the meantime. Read the list again.",
  observeOnly: "I2Q does not apply this learning. It is observed only.",
  applyOff: "I2Q applies no learning while the switch truth_learn_apply is off.",
  multiAccount:
    "The sender buys for more than one customer account. I2Q does not pair the sender with one account.",
  noRight: "Your role does not approve learnings.",
  gone: "This learning is not in your areas any more.",
  other: "Not saved. Try again.",
} as const;
